import React from "react";
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Avatar,
  useMediaQuery,
} from "@mui/material";
import {
  Person as PersonIcon,
  Description as DescriptionIcon,
  AccountBox as AccountBoxIcon,
  CircleNotifications,
} from "@mui/icons-material";
import { useLocation } from "react-router-dom";
import Card from "../common/Card";
import ChangePassword from "../operation/user/ChangePassword";
import Profile from "./Profile";
import MyAccount from "./MyAccount";
import SettingProfile from "./SettingProfile";
import { useAuthSlice } from "../../redux/slice/authSlice";
import { fullName } from "../../utils/Utility";
import { useModalStyles } from "../common/useModalStyles";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  tabValue: number;
}

export type accountProfile = {
  account: string | undefined;
};

function TabPanel(props: TabPanelProps) {
  const { children, tabValue, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={tabValue !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {tabValue === index && (
        <Box sx={{ p: { xs: 2, sm: 2.5, md: 3.5 } }}>
          {/* Caps and centers each tab's own content within the pane — keeps
              every tab (narrow forms like Change Password and wide grids like
              2FA) visually consistent instead of stretching edge to edge. */}
          <Box sx={{ maxWidth: 960, mx: "auto" }}>{children}</Box>
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const AccountProfile: React.FC = () => {
  const location = useLocation();
  const { userInfo } = useAuthSlice();
  const { tokens: mTokens, isDark } = useModalStyles();
  const [tabValue, setTabValue] = React.useState(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get("tab");
    return tab ? parseInt(tab) : 0;
  });

  const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const isDesktop = useMediaQuery("(min-width:900px)");

  const listBorderColor = isDark ? "rgba(148,163,184,0.2)" : "rgba(15,23,42,0.12)";
  const listPanelBg = isDark ? "#141720" : "rgba(255,255,255,0.86)";
  const sidebarBg = isDark
    ? "linear-gradient(180deg, rgba(20,184,166,0.07) 0%, rgba(255,255,255,0.015) 100%)"
    : "linear-gradient(180deg, rgba(20,184,166,0.05) 0%, rgba(15,23,42,0.01) 100%)";

  const shellCardSx = {
    borderRadius: "20px",
    border: `1px solid ${listBorderColor}`,
    backgroundColor: listPanelBg,
    backgroundImage: "none",
    backdropFilter: "blur(10px)",
    boxShadow: isDark ? "0 20px 48px rgba(0,0,0,0.32)" : "0 20px 40px rgba(15,23,42,0.1)",
    overflow: "hidden",
    p: "0 !important",
    flex: 1,
    display: "flex",
    minHeight: 0,
  };

  const displayName = fullName("", userInfo?.firstName, userInfo?.middleName, userInfo?.lastName);
  const initials = (displayName || "?").trim().charAt(0).toUpperCase();

  const profileDoc = (userInfo as any)?.profileDocument;
  const avatarSrc =
    profileDoc?.content && profileDoc?.fileType
      ? `data:${profileDoc.fileType};base64,${profileDoc.content}`
      : undefined;

  const tabItems = [
    { icon: <PersonIcon />, label: "Profile", helper: "Personal details" },
    { icon: <DescriptionIcon />, label: "2FA", helper: "Two-factor security" },
    { icon: <AccountBoxIcon />, label: "Change Password", helper: "Login credentials" },
    { icon: <CircleNotifications />, label: "Notifications", helper: "Alert preferences" },
  ];

  return (
    <Box
      sx={{
        mx: { xs: "10px", sm: "20px" },
        mt: "12px",
        pb: { xs: "16px", md: "20px" },
        height: { xs: "auto", md: "calc(100vh - 100px)" },
        minHeight: { xs: "100vh", md: 0 },
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box sx={{ mb: 2, flexShrink: 0 }} data-testid="header">
        <Typography
          sx={{
            fontSize: "0.7rem",
            fontWeight: 700,
            color: mTokens.accent,
            textTransform: "uppercase",
            letterSpacing: "0.12em",
            mb: 0.3,
          }}
        >
          Account
        </Typography>
        <Typography
          component="h1"
          sx={{
            fontSize: { xs: "1.35rem", sm: "1.6rem" },
            fontWeight: 800,
            color: mTokens.textPrimary,
            lineHeight: 1.1,
          }}
        >
          Account Profile
        </Typography>
        <Typography sx={{ fontSize: "0.78rem", color: mTokens.textSecondary, mt: 0.4 }}>
          Manage your profile, security, and notification preferences.
        </Typography>
      </Box>

      <Card sx={shellCardSx}>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: "stretch",
            width: "100%",
            minHeight: 0,
          }}
        >
          <Box
            sx={{
              width: { xs: "100%", md: 272 },
              flexShrink: 0,
              backgroundImage: sidebarBg,
              borderRight: { xs: "none", md: `1px solid ${mTokens.border}` },
              borderBottom: { xs: `1px solid ${mTokens.border}`, md: "none" },
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                px: { xs: 2, sm: 3 },
                py: { xs: 2.5, sm: 3.5 },
                borderBottom: `1px solid ${mTokens.border}`,
              }}
            >
              <Avatar
                src={avatarSrc}
                sx={{
                  width: 72,
                  height: 72,
                  fontSize: "1.6rem",
                  fontWeight: 800,
                  bgcolor: mTokens.accent,
                  color: "#fff",
                  mb: 1.25,
                  border: `3px solid ${isDark ? "rgba(20,184,166,0.28)" : "rgba(20,184,166,0.18)"}`,
                  boxShadow: `0 0 0 4px ${isDark ? "rgba(20,184,166,0.1)" : "rgba(20,184,166,0.08)"}`,
                }}
              >
                {initials}
              </Avatar>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 800,
                  color: mTokens.textPrimary,
                  textAlign: "center",
                  fontSize: { xs: "1.05rem", sm: "1.15rem" },
                }}
              >
                {displayName}
              </Typography>
              <Box
                sx={{
                  mt: 0.7,
                  px: 1.1,
                  py: 0.25,
                  borderRadius: "999px",
                  backgroundColor: isDark ? "rgba(20,184,166,0.14)" : "rgba(20,184,166,0.1)",
                }}
              >
                <Typography
                  sx={{
                    textTransform: "uppercase",
                    color: mTokens.accent,
                    fontWeight: 700,
                    fontSize: "0.68rem",
                    letterSpacing: "0.06em",
                  }}
                >
                  {userInfo?.roleDisplayValue}
                </Typography>
              </Box>
            </Box>

            <Tabs
              value={tabValue}
              onChange={handleChange}
              orientation={isDesktop ? "vertical" : "horizontal"}
              variant={isDesktop ? "standard" : "scrollable"}
              scrollButtons={isDesktop ? false : "auto"}
              allowScrollButtonsMobile
              sx={{
                px: { xs: 0.5, md: 1.5 },
                py: { xs: 0, md: 1.5 },
                flex: { xs: "none", md: 1 },
                "& .MuiTabs-indicator": isDesktop
                  ? { display: "none" }
                  : { backgroundColor: mTokens.accent, height: 3, borderRadius: "3px 3px 0 0" },
                "& .MuiTabs-flexContainer": { gap: { xs: 0, md: 0.5 } },
                "& .MuiTab-root": {
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: "0.86rem",
                  minHeight: { xs: 52, md: 54 },
                  alignItems: { xs: "center", md: "flex-start" },
                  justifyContent: "flex-start",
                  textAlign: "left",
                  borderRadius: { xs: 0, md: "12px" },
                  border: { xs: "none", md: "1px solid transparent" },
                  px: { xs: 1.5, md: 1.6 },
                  color: mTokens.textSecondary,
                  transition: "background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease",
                  "&:hover": {
                    backgroundColor: isDark ? "rgba(20,184,166,0.07)" : "rgba(20,184,166,0.05)",
                    color: mTokens.accent,
                  },
                  "&.Mui-selected": {
                    color: mTokens.accent,
                    backgroundColor: isDesktop
                      ? isDark
                        ? "rgba(20,184,166,0.12)"
                        : "rgba(20,184,166,0.08)"
                      : "transparent",
                    borderColor: isDesktop
                      ? isDark
                        ? "rgba(20,184,166,0.28)"
                        : "rgba(20,184,166,0.22)"
                      : "transparent",
                  },
                  "& .MuiTab-iconWrapper": { fontSize: "1.15rem" },
                },
                "& .MuiTabs-scrollButtons": { color: mTokens.textSecondary },
              }}
            >
              {tabItems.map((tab, index) => (
                <Tab
                  key={tab.label}
                  icon={tab.icon}
                  iconPosition="start"
                  label={
                    isDesktop ? (
                      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", lineHeight: 1.25 }}>
                        <Box component="span">{tab.label}</Box>
                        <Box
                          component="span"
                          sx={{
                            fontSize: "0.68rem",
                            fontWeight: 500,
                            color: mTokens.textSecondary,
                            opacity: tabValue === index ? 0.85 : 0.65,
                          }}
                        >
                          {tab.helper}
                        </Box>
                      </Box>
                    ) : (
                      tab.label
                    )
                  }
                  {...a11yProps(index)}
                />
              ))}
            </Tabs>
          </Box>

          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              overflowY: "auto",
            }}
          >
            <TabPanel tabValue={tabValue} index={0}>
              <Profile />
            </TabPanel>
            <TabPanel tabValue={tabValue} index={1}>
              <MyAccount />
            </TabPanel>
            <TabPanel tabValue={tabValue} index={2}>
              <ChangePassword embedded />
            </TabPanel>
            <TabPanel tabValue={tabValue} index={3}>
              <SettingProfile />
            </TabPanel>
          </Box>
        </Box>
      </Card>
    </Box>
  );
};

export default AccountProfile;
